import { useState } from "react";
import type { ContentItem } from "@/types/content";
import { useContentExplorer } from "@/hooks/useContentExplorer";
import { getAllGenres, getAllRegions, getAllYears } from "@/utils/content";
import FilterSidebar from "@/components/feature/FilterSidebar";
import SortBar from "@/components/feature/SortBar";
import ContentListItem from "@/components/feature/ContentListItem";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";

export default function CategoryExplorer({ items }: { items: ContentItem[] }) {
  const explorer = useContentExplorer(items);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const regionOptions = getAllRegions();
  const yearOptions = getAllYears().map(String);
  const genreOptions = getAllGenres();

  const filterProps = {
    regionOptions,
    yearOptions,
    genreOptions,
    selected: explorer.selected,
    onToggle: (type: "region" | "year" | "genre", value: string) => {
      if (type === "region") explorer.toggleRegion(value);
      if (type === "year") explorer.toggleYear(value);
      if (type === "genre") explorer.toggleGenre(value);
    },
    onClear: explorer.clearFilters,
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
      <aside className="hidden lg:block lg:w-64 shrink-0">
        <div className="sticky top-24">
          <FilterSidebar {...filterProps} />
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <SortBar
          sort={explorer.sort}
          onChange={explorer.setSort}
          total={explorer.total}
          onOpenFilter={() => setDrawerOpen(true)}
        />

        {explorer.paged.length > 0 ? (
          <>
            <div className="flex flex-col gap-3.5">
              {explorer.paged.map((item, i) => (
                <ContentListItem key={item.id} item={item} index={i} />
              ))}
            </div>
            <Pagination current={explorer.page} total={explorer.totalPages} onChange={explorer.setPage} />
          </>
        ) : (
          <EmptyState
            title="没有符合条件的资源"
            description="试试调整或清空筛选条件，也许会找到更多感兴趣的国语影音作品。"
            actionLabel="清空筛选"
            onAction={explorer.clearFilters}
          />
        )}
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex flex-col justify-end">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-foreground-950/50"
          />
          <div className="relative bg-background-50 rounded-t-2xl max-h-[80vh] overflow-y-auto p-4 pb-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-semibold text-foreground-900 font-heading">筛选条件</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl text-foreground-600" />
              </button>
            </div>
            <FilterSidebar {...filterProps} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full rounded-md bg-primary-600 py-3 text-sm font-medium text-background-50 hover:bg-primary-700 transition-colors cursor-pointer"
            >
              查看 {explorer.total} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}